import React, { useEffect, useRef, useState } from 'react';
import { FaHistory, FaPlay, FaRegBookmark, FaTimes } from 'react-icons/fa';
import axiosInstance from '../../utility/axiosInstance';
import ResultGrid from './ResultGrid';
import {
    BAD_ANSWER, DataFailure, DataResult, REQUEST_TIMEOUT_MS, SQL_MAX_CHARS, classifyDataError, groupThousands, readResult,
} from './dataView';
import { useConfirm } from '../ConfirmContext';
import { isReadOnlyRefusal, newWriteRef, readWriteFailure, readWriteSuccess, settleUrl, settleWrite, settledLine } from './writeErrors';
import {
    HistoryEntry, SavedQuery, addToHistory, readHistory, readSavedQueries, savedQueriesUrl, savedQueryErrorLine, savedQueryUrl, sqlPreview, writeHistory,
} from './sqlLibrary';

interface SqlBoxProps {
    databaseId: number;
    /** The SQL text lives in the view, so it survives the view switching to its unavailable state and back. */
    sql: string;
    onSqlChange: (sql: string) => void;
    /** Called when the database cannot be read at all: the view replaces itself with the unavailable state. */
    onUnavailable: (failure: DataFailure) => void;
    /** E24: when editing is on, a statement the read-only transaction refuses can be run as a write, after a confirm. */
    canWrite: boolean;
    /** The view re-reads the table list after a write that may have changed the schema. */
    onWrote?: () => void;
    /** E25: where this browser keeps the history (null: no history, nothing else changes). */
    storage?: Storage | null;
}

type RunState =
    | { status: 'idle' }
    | { status: 'running' }
    | { status: 'done'; result: DataResult }
    | { status: 'failed'; failure: DataFailure }
    | { status: 'wrote'; line: string };

/**
 * One read-only SQL statement and its result (DESIGN_V1.md D13). Read-only is the helper's, three times over (the
 * role's grants, the session default and a READ ONLY transaction); this box never claims more than that.
 *
 * ⛔ No Idempotency-Key header, ever: the answer is the customer's rows, and a stored replay is exactly what the plan
 *    rules out (the backend also refuses to cache these routes). The result lives in component state only. The text
 *    also goes to this browser's history, and to the platform when the user saves it (E25, Milos 2026-10-25).
 */
const sleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

/**
 * The line after a SQL write: a plain count for a statement that changes rows, else the command tag (CREATE TABLE has
 * an affected count of 0, which is not "0 rows changed").
 */
export const writtenLine = (tag: string | undefined, affected: number | null): string => {
    const rowsVerb = /^(INSERT|UPDATE|DELETE|MERGE|COPY)\b/.test(tag ?? '');
    if (typeof affected === 'number' && (rowsVerb || !tag)) return `Done. ${affected === 1 ? '1 row' : `${affected} rows`} changed.`;
    const verb = (tag ?? '').replace(/\s+\d+(\s+\d+)?$/, '');   // "SELECT 3" -> "SELECT"
    return verb ? `Done. ${verb}.` : 'Done.';
};

const SqlBox: React.FC<SqlBoxProps> = ({ databaseId, sql, onSqlChange, onUnavailable, canWrite, onWrote, storage = null }) => {
    const [run, setRun] = useState<RunState>({ status: 'idle' });
    // E25: saved queries (the platform, this user's, this database's) and history (this browser only)
    const [history, setHistory] = useState<HistoryEntry[]>(() => readHistory(storage, databaseId));
    const [saved, setSaved] = useState<SavedQuery[] | 'loading' | 'failed'>('loading');
    const [loaded, setLoaded] = useState<SavedQuery | null>(null);   // the saved query the box's text came from
    const [naming, setNaming] = useState<string | null>(null);       // the name being typed after Save
    const [libLine, setLibLine] = useState<string | null>(null);
    const [libBusy, setLibBusy] = useState(false);
    const [menu, setMenu] = useState<'saved' | 'history' | null>(null);
    const menusRef = useRef<HTMLDivElement | null>(null);
    const { confirm } = useConfirm();
    // An answer that arrives after the view has gone (Back, or the unavailable state) is dropped.
    // Set in the setup, not only at creation: StrictMode runs setup, cleanup, setup.
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const loadSaved = async () => {
        try {
            const res = await axiosInstance.get(savedQueriesUrl(databaseId), { timeout: REQUEST_TIMEOUT_MS });
            if (mounted.current) setSaved(readSavedQueries(res.data) ?? 'failed');
        } catch {
            if (mounted.current) setSaved('failed');
        }
    };
    useEffect(() => { void loadSaved(); }, [databaseId]);   // eslint-disable-line react-hooks/exhaustive-deps

    // A click outside the two menus closes them
    useEffect(() => {
        if (menu === null) return;
        const close = (e: MouseEvent) => { if (menusRef.current && !menusRef.current.contains(e.target as Node)) setMenu(null); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [menu]);

    const canRun = sql.trim().length > 0 && run.status !== 'running';
    const changed = loaded !== null && sql.trim() !== loaded.sql.trim();
    const sortByName = (list: SavedQuery[]) => [...list].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()) || a.id - b.id);

    const pick = (text: string, from: SavedQuery | null) => {
        onSqlChange(text);
        setLoaded(from);
        setMenu(null);
        setNaming(null);
        setLibLine(null);
    };
    const clearHistory = () => {
        setHistory([]);
        writeHistory(storage, databaseId, []);
        setMenu(null);
    };

    /** Save the box's statement under a new name. */
    const saveNew = async () => {
        const name = (naming ?? '').trim();
        if (!name || !sql.trim() || libBusy) return;
        setLibBusy(true);
        setLibLine(null);
        try {
            const res = await axiosInstance.post(savedQueriesUrl(databaseId), { name, sql }, { timeout: REQUEST_TIMEOUT_MS });
            if (!mounted.current) return;
            const q = readSavedQueries({ queries: [res.data?.query] })?.[0];
            if (q) {
                setSaved((cur) => (Array.isArray(cur) ? sortByName([...cur, q]) : cur));
                setLoaded(q);
            }
            setNaming(null);
            setLibLine('Saved.');
        } catch (err) {
            if (mounted.current) setLibLine(savedQueryErrorLine(err));
        } finally {
            if (mounted.current) setLibBusy(false);
        }
    };

    /** Put the box's statement into the saved query it came from. */
    const updateLoaded = async () => {
        if (!loaded || !sql.trim() || libBusy) return;
        setLibBusy(true);
        setLibLine(null);
        try {
            const res = await axiosInstance.patch(savedQueryUrl(databaseId, loaded.id), { sql }, { timeout: REQUEST_TIMEOUT_MS });
            if (!mounted.current) return;
            const q = readSavedQueries({ queries: [res.data?.query] })?.[0];
            if (q) {
                setSaved((cur) => (Array.isArray(cur) ? sortByName(cur.map((x) => (x.id === q.id ? q : x))) : cur));
                setLoaded(q);
            }
            setLibLine('Saved.');
        } catch (err) {
            if (mounted.current) setLibLine(savedQueryErrorLine(err));
        } finally {
            if (mounted.current) setLibBusy(false);
        }
    };

    const deleteSaved = async (q: SavedQuery) => {
        setMenu(null);
        const go = await confirm({ title: 'Delete saved query', message: `Delete ${q.name}?`, confirmText: 'Delete', cancelText: 'Cancel', type: 'danger' });
        if (!go || !mounted.current) return;
        try {
            await axiosInstance.delete(savedQueryUrl(databaseId, q.id), { timeout: REQUEST_TIMEOUT_MS });
            if (!mounted.current) return;
            setSaved((cur) => (Array.isArray(cur) ? cur.filter((x) => x.id !== q.id) : cur));
            if (loaded?.id === q.id) setLoaded(null);
            setLibLine('Deleted.');
        } catch (err) {
            if (!mounted.current) return;
            if ((err as any)?.response?.data?.error?.code === 'query_not_found') setSaved((cur) => (Array.isArray(cur) ? cur.filter((x) => x.id !== q.id) : cur));
            setLibLine(savedQueryErrorLine(err));
        }
    };

    const execute = async () => {
        if (!canRun) return;
        // E25: every statement run goes on top of this browser's history (never to the platform)
        const next = addToHistory(history, sql, new Date().toISOString());
        setHistory(next);
        writeHistory(storage, databaseId, next);
        setRun({ status: 'running' });
        try {
            const res = await axiosInstance.post(`/v1/databases/${databaseId}/data/query`, { sql }, { timeout: REQUEST_TIMEOUT_MS });
            if (!mounted.current) return;
            const result = readResult(res.data);
            if (!result) { onUnavailable(BAD_ANSWER); return; }
            setRun({ status: 'done', result });
        } catch (err) {
            if (!mounted.current) return;
            const failure = classifyDataError(err);
            if (failure.kind === 'unavailable') { onUnavailable(failure); return; }
            // E24: a write refused only because the transaction was read-only. With editing on, offer to run it.
            if (canWrite && failure.kind === 'query' && isReadOnlyRefusal(failure.detail)) {
                const go = await confirm({ title: 'Run this statement?', message: 'This changes data. Run it?', confirmText: 'Run', cancelText: 'Cancel', type: 'danger' });
                if (!mounted.current) return;
                if (go) { await runWrite(); return; }
                setRun({ status: 'idle' });
                return;
            }
            setRun({ status: 'failed', failure });
        }
    };

    /** Send the statement as a write (E24): once, with a write_ref; an answer that does not say is settled, never resent. */
    const runWrite = async () => {
        setRun({ status: 'running' });
        const writeRef = newWriteRef();
        let outcome;
        try {
            const res = await axiosInstance.post(`/v1/databases/${databaseId}/data/sql-write`, { write_ref: writeRef, sql }, { timeout: REQUEST_TIMEOUT_MS });
            outcome = readWriteSuccess(res?.status, res?.data);
        } catch (err) {
            outcome = readWriteFailure(err, 'update');
        }
        if (!mounted.current) return;
        if (outcome.kind === 'refused') { setRun({ status: 'wrote', line: outcome.line }); return; }
        if (outcome.kind === 'applied') {
            setRun({ status: 'wrote', line: writtenLine(outcome.tag, outcome.affected) });
            onWrote?.();
            return;
        }
        // unknown: settle by the write_ref, never resend
        const settled = await settleWrite((url) => axiosInstance.get(url, { timeout: REQUEST_TIMEOUT_MS }).then((r) => r.data), settleUrl(databaseId, writeRef), sleep);
        if (!mounted.current) return;
        setRun({ status: 'wrote', line: settledLine(settled) });
        if (settled !== 'not_applied') onWrote?.();
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            execute();
        }
    };

    return (
        <div className="db-data-pane db-data-sql">
            <div className="db-data-pane-header">
                <label className="db-data-pane-title" htmlFor={`db-data-sql-${databaseId}`}>SQL</label>
                <div className="db-data-pane-actions db-data-sql-menus" ref={menusRef}>
                    <div className="db-data-sql-menu">
                        <button type="button" className="db-data-text-button" aria-expanded={menu === 'saved'} onClick={() => setMenu((m) => (m === 'saved' ? null : 'saved'))}>
                            <FaRegBookmark aria-hidden="true" /> Saved{Array.isArray(saved) ? ` (${saved.length})` : ''}
                        </button>
                        {menu === 'saved' && (
                            <div className="db-data-sql-menu-items" role="menu" aria-label="Saved queries">
                                {saved === 'loading' ? <p className="db-data-sql-menu-note">Loading...</p>
                                    : saved === 'failed' ? <p className="db-data-sql-menu-note">Could not read saved queries.</p>
                                    : saved.length === 0 ? <p className="db-data-sql-menu-note">None yet. Write a statement, then Save.</p>
                                    : saved.map((q) => (
                                        <div className="db-data-sql-menu-row" key={q.id}>
                                            <button type="button" role="menuitem" className="db-data-sql-menu-pick" title={q.sql} onClick={() => pick(q.sql, q)}>{q.name}</button>
                                            <button type="button" className="db-data-sql-menu-remove" aria-label={`Delete saved query ${q.name}`} title="Delete" onClick={() => void deleteSaved(q)}>
                                                <FaTimes />
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                    <div className="db-data-sql-menu">
                        <button type="button" className="db-data-text-button" aria-expanded={menu === 'history'} onClick={() => setMenu((m) => (m === 'history' ? null : 'history'))}>
                            <FaHistory aria-hidden="true" /> History
                        </button>
                        {menu === 'history' && (
                            <div className="db-data-sql-menu-items" role="menu" aria-label="History">
                                {history.length === 0 ? <p className="db-data-sql-menu-note">Nothing run yet in this browser.</p> : (
                                    <>
                                        {history.map((e) => (
                                            <button type="button" role="menuitem" key={e.at + e.sql} className="db-data-sql-menu-pick" title={e.sql} onClick={() => pick(e.sql, null)}>
                                                {sqlPreview(e.sql)}
                                            </button>
                                        ))}
                                        <button type="button" className="db-data-sql-menu-clear" onClick={clearHistory}>Clear history</button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <textarea
                id={`db-data-sql-${databaseId}`}
                className="db-data-sql-input"
                value={sql}
                onChange={(e) => onSqlChange(e.target.value)}
                onKeyDown={onKeyDown}
                maxLength={SQL_MAX_CHARS}
                rows={5}
                spellCheck={false}
                autoComplete="off"
                placeholder="SELECT * FROM my_table LIMIT 50"
            />
            <div className="db-data-sql-actions">
                <button type="button" className="submit-button" onClick={execute} disabled={!canRun} title="Ctrl+Enter">
                    <FaPlay /> {run.status === 'running' ? 'Running...' : 'Run'}
                </button>
                {naming === null ? (
                    <>
                        <button type="button" className="db-data-text-button" onClick={() => { setNaming(''); setLibLine(null); }} disabled={!sql.trim() || libBusy}>
                            {loaded ? 'Save as new' : 'Save'}
                        </button>
                        {loaded && changed && (
                            <button type="button" className="db-data-text-button" onClick={() => void updateLoaded()} disabled={libBusy}>
                                Update {loaded.name}
                            </button>
                        )}
                        {loaded && !changed && <span className="db-data-sql-loaded" title="The saved query in the box">{loaded.name}</span>}
                    </>
                ) : (
                    <span className="db-data-sql-naming">
                        <input
                            aria-label="Name for this query"
                            placeholder="Name"
                            value={naming}
                            maxLength={100}
                            autoFocus
                            disabled={libBusy}
                            onChange={(e) => setNaming(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); void saveNew(); }
                                if (e.key === 'Escape') { setNaming(null); setLibLine(null); }
                            }}
                        />
                        <button type="button" className="db-data-text-button db-data-create-button" onClick={() => void saveNew()} disabled={!naming.trim() || libBusy}>
                            {libBusy ? 'Saving...' : 'Save'}
                        </button>
                        <button type="button" className="db-data-text-button" onClick={() => { setNaming(null); setLibLine(null); }} disabled={libBusy}>Cancel</button>
                    </span>
                )}
                {libLine && <span className="db-data-sql-lib-line" role="status">{libLine}</span>}
            </div>

            {run.status === 'failed' && (
                <div className="db-data-error" role="alert">
                    {run.failure.detail
                        ? <pre className="db-data-error-detail">{run.failure.detail}</pre>
                        : <p className="db-data-error-message" title={run.failure.apiMessage}>{run.failure.message}</p>}
                </div>
            )}

            {run.status === 'wrote' && (
                <p className="db-data-result-summary" role="status">{run.line}</p>
            )}

            {run.status === 'done' && (
                <div className="db-data-result">
                    {run.result.columns.length > 0 && (
                        <p className="db-data-result-summary">
                            {run.result.rows.length === 1 ? '1 row' : `${groupThousands(run.result.rows.length)} rows`}
                        </p>
                    )}
                    <ResultGrid
                        label="Query result"
                        columns={run.result.columns}
                        rows={run.result.rows}
                        emptyText="No rows."
                        truncatedText={run.result.truncated
                            ? (run.result.rows.length === 1 ? 'First row only.' : `First ${groupThousands(run.result.rows.length)} rows.`)
                            : null}
                    />
                </div>
            )}
        </div>
    );
};

export default SqlBox;
